import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Switch } from '@repo/ui';

import { font } from 'settings/tokens';
import { stateCardSurfaceSx } from './stateCardSurface';

/**
 * VRG-511 — "you previously owned this", plus the one control a past owner has.
 *
 * ## Why this card exists at all
 *
 * Without it, a past owner opening an item they used to hold gets the PUBLIC page,
 * byte-identical to what a stranger tapping the tag sees. Nothing says the item was
 * ever theirs, and — the part that actually misleads — Gift, Sell, Verify and
 * Transfer are simply absent, which reads as a broken version of the page they
 * remember rather than as a different, correct one.
 *
 * Those four are hidden rather than disabled, and that is right (working-conventions
 * §46: record state hides, permission disables). But §46's other half is the
 * obligation this card discharges — hiding is only correct when the surface still
 * explains itself. A page that silently drops four controls has hidden them; it has
 * not explained them.
 *
 * ## Why the toggle lives here and not in view settings
 *
 * `ViewSettingsDrawer` is the CURRENT owner's control over their own certificate:
 * it is reached from Share, and it governs what the item publishes. A past owner
 * has no such standing — they cannot share the item, cannot change what it shows,
 * and cannot reach that drawer. The only thing they still govern is their own name
 * on the chain, so it is the only thing this card offers.
 *
 * ## ⚠️ THE DEFAULTS ARE OPPOSITE TO `show_ownership`, ON PURPOSE
 *
 * `show_ownership` (the current owner's identity block) defaults OFF. This defaults
 * ON. Same English word, different objects: that is a spotlight on a page you still
 * hold, this is a link in a chain whose credibility is the product. Do not
 * "harmonise" the two, and do not reuse `show_ownership` for this — see
 * `applyPublicProvenanceConsent` on the API side for the full argument.
 *
 * ## Design status — REPORTED, NOT INVENTED
 *
 * The cached Figma frame (`4541-788857`) covers `show_ownership` only, as the
 * current owner's control over their own card. **There is no design for this
 * surface.** So nothing here is invented: the container is `stateCardSurfaceSx`,
 * the app's existing state-card surface, and the toggle row is
 * `ViewSettingsDrawer`'s row markup verbatim — title, `text.secondary` description
 * at `lineHeight: 1.5` (§15), `Switch` from `@repo/ui`, aligned to `flex-start`.
 * Placement, copy and whether this should be a card at all are decisions for
 * review, not settled by this file.
 */
export const PreviouslyOwnedCard = ({
  sharesIdentity,
  onChange,
  disabled = false,
}: {
  sharesIdentity: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) => {
  const theme = useTheme();

  return (
    <Box sx={stateCardSurfaceSx}>
      {/* The state-card family's heading treatment, one size for all of them. Do not
          raise it here alone — the cards are meant to read as one family. */}
      <Typography sx={{ fontSize: font.body, fontWeight: 500, mb: 1.5 }}>
        You previously owned this
      </Typography>
      {/* Separate sentences rather than em-dash-joined clauses, and lineHeight 1.5 so
          descriptive copy does not read dense (§15).

          It says what the reader can still DO, because the question this card answers
          is "why does this look different" and "the actions are gone" is only half
          the answer. */}
      <Typography
        sx={{ fontSize: font.label, lineHeight: 1.5 }}
        color='text.secondary'
      >
        This item has moved on to another owner. You can no longer gift, sell or
        transfer it, and you still choose whether your name appears in its history.
      </Typography>

      {/* 20px above the control, the same proportion the sibling cards use: everything
          above DESCRIBES the state and the control ACTS on it, so that gap is the
          card's one real boundary. `apps/app` runs MUI's 8px base, so 2.5 is 20px —
          a factor copied from a platform file would render 40 (§29). */}
      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        spacing={2}
        sx={{
          mt: 2.5,
          pt: 2.5,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: font.body, mb: 0.5 }}>
            Show my name in this item's history
          </Typography>
          {/* The consequence, stated in the reader's terms, because the control cannot
              say it (§43's argument, applied to a toggle rather than to a Save). Both
              directions are named: this is reversible, and re-revealing is allowed, so
              hiding that half would misrepresent the choice as one-way. */}
          <Typography
            sx={{ fontSize: font.label, lineHeight: 1.5 }}
            color='text.secondary'
          >
            {sharesIdentity
              ? 'Anyone who opens this item sees you as a previous owner. Turn this off and your name is replaced with "Previous owner". Nothing is deleted, and you can turn it back on at any time.'
              : 'You appear as "Previous owner" to anyone who opens this item. Turn this on and your name is shown again.'}
          </Typography>
        </Box>
        <Switch
          sx={{ mt: '-2px' }}
          checked={sharesIdentity}
          disabled={disabled}
          onChange={(_, checked) => onChange(checked)}
        />
      </Stack>
    </Box>
  );
};

export default PreviouslyOwnedCard;
